import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import Dropdown from 'react-bootstrap/Dropdown'
import styled from 'styled-components'
import { colors } from 'globalStyles'
import MenuIcon from '@mui/icons-material/Menu'
import { useTranslation } from 'react-i18next'
import SVGIcon from 'reusableComponents/SVGIcon'
import { ReactComponent as CircleXmark } from 'assets/svg/circle-xmark.svg'
import { getDOMEncodeString } from 'utils/helperFunctions'
import { ReactComponent as CircleIcon } from 'assets/svg/circle.svg'

const HamburgerButton = styled.button`
  height: 36px;
  width: 36px;
  border-radius: 100%;
  border: unset;
  padding: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 250ms;
  background-color: ${(props) =>
    props.isClosed ? colors.primary_white : colors.medium_dark_shade_blue};
`

const DropDownStyled = styled(Dropdown)`
  display: flex;
  max-width: 310px;
  margin-left: 18px;
  .dropdown-divider {
    background-color: ${colors.light_blue};
  }
  .dropdown-menu {
    margin-top: 12px;
    margin-right: -15px;
  }
  .dropdown-item {
    width: 290px;
    height: 54px;
    padding: 15px 12px !important;
    font-size: 20px;
    gap: 10px;
  }

  .dropdown-item:focus {
    outline: 4px solid ${colors.primary_blue};
  }
  .dropdown-item:hover {
    color: ${colors.primary_blue} !important;
    background-color: ${colors.primary_white} !important;
  }
`
const circleIconStyle = {
  width: '10px',
  height: '10px',
  color: colors.primary_red,
  marginLeft: '-5px',
  marginTop: '12px',
}

const CustomToggleWithHamburgerMenu = React.forwardRef(({ onClick, showRedDot, isOpen }, ref) => {
  const [showCircleIcon, setShowCircleIcon] = useState(true)
  const hasClicked = useRef(false)
  const { t } = useTranslation('translation', { keyPrefix: 'aria_labels' })
  return (
    <>
      <HamburgerButton
        aria-label={t('hamburger_menu_button')}
        ref={ref}
        onClick={(e) => {
          e.preventDefault()
          // Check if this is the first click
          if (!hasClicked.current) {
            setShowCircleIcon(false) // Hide CircleIcon on the first click
            hasClicked.current = true // Mark as clicked
          }
          onClick(e)
        }}
        isClosed={isOpen}
      >
        {isOpen ? (
          <SVGIcon
            Icon={CircleXmark}
            color={colors.blue_dark}
            style={{ height: '36px', width: '36px' }}
          />
        ) : (
          <MenuIcon style={{ color: colors.primary_white }} />
        )}
      </HamburgerButton>
      {showRedDot && showCircleIcon && <CircleIcon style={circleIconStyle} />}
    </>
  )
})

CustomToggleWithHamburgerMenu.propTypes = {
  onClick: PropTypes.func.isRequired,
  showRedDot: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool,
}

CustomToggleWithHamburgerMenu.defaultProps = {
  isOpen: false,
}

const DropDownForHeader = ({ dropdownItems, showRedDot }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const [show, setShow] = useState(false)

  const handleItemClick = (onItemClick) => {
    if (onItemClick) {
      onItemClick()
    }
    setShow(false) // Close the dropdown after item is clicked
  }

  return (
    <DropDownStyled show={show} onToggle={(isOpen) => setShow(isOpen)}>
      <Dropdown.Toggle as={CustomToggleWithHamburgerMenu} showRedDot={showRedDot} isOpen={show} />
      <Dropdown.Menu>
        {dropdownItems.map((item, index) => (
          <React.Fragment key={index}>
            <Dropdown.Item
              onClick={() => handleItemClick(item.onItemClick)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {item.label}
              {item.icon && !item.hoveredIcon && (
                <img
                  src={getDOMEncodeString(
                    <SVGIcon
                      Icon={item.icon}
                      color={hoveredIndex === index ? `${colors.primary_blue}` : item.color}
                    />
                  )}
                  style={item.iconStyle}
                  alt="SVG Icon"
                />
              )}
              {item.icon && item.hoveredIcon && (
                <img
                  src={getDOMEncodeString(
                    <SVGIcon Icon={hoveredIndex === index ? item.hoveredIcon : item.icon} />
                  )}
                  style={item.iconStyle}
                  alt="SVG Icon"
                />
              )}
            </Dropdown.Item>
            {index < dropdownItems.length - 1 && <Dropdown.Divider />}
          </React.Fragment>
        ))}
      </Dropdown.Menu>
    </DropDownStyled>
  )
}

DropDownForHeader.propTypes = {
  dropdownItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onItemClick: PropTypes.func.isRequired,
      icon: PropTypes.elementType,
      hoveredIcon: PropTypes.elementType,
      // eslint-disable-next-line react/forbid-prop-types
      iconStyle: PropTypes.object,
      color: PropTypes.string,
    })
  ).isRequired,
  showRedDot: PropTypes.bool,
}

DropDownForHeader.defaultProps = {
  showRedDot: false,
}

export default DropDownForHeader
